import React, { useEffect, useCallback, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import {
  useUniqueId,
  NavigationTrap,
  NavigationTrapProps,
  mergeRefs,
} from '@ds/react-utils'
import { AnimationDefinition, MotionPresence, MotionVariant } from '@ds/motion'
import StopPropagation, {
  EventListenerTypes,
} from '../../internal/components/StopPropagation'
import { consoleWarn } from '../../logging'
import { CustomPropTypes } from '../../support'
import type { ConditionalReactElementChildOrArray } from '../../types'
import { bodyScroll, EscapeToClose } from '../../utilities'
import { globalIds } from '../../variables'
import { Scrim } from '../Scrim'
import { ModalBase, ModalBaseProps } from './ModalBase'
import { ModalClose } from './ModalClose'
import { ModalHeader, ModalHeaderProps } from './ModalHeader'
import { ModalBody, ModalBodyProps } from './ModalBody'
import { ModalFooter, ModalFooterProps } from './ModalFooter'
import { ModalPortal } from './ModalPortal'

export const modalHeights = ['content', 'fixed', 'window'] as const
export type ModalHeight = (typeof modalHeights)[number]

export const modalWidths = ['xlarge', 'large', 'medium', 'small'] as const
export type ModalWidth = (typeof modalWidths)[number]

export const modalRoles = ['alertdialog', 'dialog'] as const
export type ModalRoles = (typeof modalRoles)[number]

export interface ModalProps
  extends Omit<ModalBaseProps, 'disabled' | 'children'> {
  /**
   * The children of the Modal
   */
  children: ConditionalReactElementChildOrArray<
    ModalHeaderProps | ModalBodyProps | ModalFooterProps
  >
  /**
   * Whether the Modal should stop the propagation of all events.
   */
  disableEventPropagation?: boolean
  /**
   * An array of React events that will be allowed to propagate up the DOM tree past the Modal. If an event, or events are passed, the Modal will stop propagation of all other events.
   * @example {['onClick']}
   */
  eventsToPropagate?: Array<keyof EventListenerTypes>
  /**
   * A selector or ref specifying the DOM element to set initial focus on once the Modal animates in.
   *
   * Default initial focus is at the top of the Modal.
   * This prop can be used to customize the initial focus,
   * particularly for modals which contain an error message, alert, or warning.
   * In these cases, initial focus should be the least destructive button, such as "Stay Logged In" or "Cancel".
   *
   * See the [Alert Modal configuration](./index.html?path=/docs/components-modal-configurations-developer--alert-modal) for an example.
   */
  initialFocus?: string | React.RefObject<HTMLElement>
  /**
   * @ignore @internal Used to determine if the Modal is a child of a ModalPortal
   */
  _modalPortalChild?: boolean
  /**
   * A function to invoke when the Modal requests to be closed.
   *
   * This request is made when:
   *   - the "Escape" key is pressed
   *   - the document receives a click outside of the Modal
   */
  onClose?: (event: Event | React.SyntheticEvent) => void
  /**
   * A function to invoke when the Modal is fully loaded in.
   */
  onOpen?: () => void
  /**  A function to invoke when the Modal animation out completes */
  onHideComplete?: () => void
  /**  A function to invoke when the Modal animation in completes */
  onShowComplete?: () => void
  /**
   * Whether the Modal is shrouded by another Modal displayed above it.
   *
   * The effect is to disable the Modal and dismiss the associated Scrim.
   */
  shrouded?: boolean
  /**
   * Displays the modal.
   */
  visible?: boolean
}

type TrapConfig = Partial<NavigationTrapProps>

const initialTrapConfig: TrapConfig = {
  enabled: false,
  initialElement: undefined,
}

const ENTER_SCALE = 'enterScale'
const EXIT_SCALE = 'exitScale'

/**
 * Modals function as a method to temporarily focus the user’s attention away from all other tasks in order to communicate a brief message or to complete a short series of actions.
 */
export function Modal({
  accessibilityTitle,
  children,
  closeButton,
  disableEventPropagation = false,
  eventsToPropagate,
  forwardedRef,
  height = 'content',
  initialFocus,
  _modalPortalChild,
  onClose,
  onOpen,
  onHideComplete,
  onShowComplete,
  role = 'dialog',
  shrouded = false,
  visible = false,
  width = 'medium',
  ...restProps
}: ModalProps) {
  const [modal, setModal] = useState<HTMLElement | null>(null)
  const modalCallbackRef = (element: HTMLElement | null) => setModal(element)
  const mountedRef = useRef<boolean>()

  const defaultInitialFocusId = useUniqueId('short', 'defaultInitialFocus-')

  const [trapConfig, setTrapConfig] = useState<TrapConfig>(initialTrapConfig)

  const shouldStopPropagation =
    disableEventPropagation || !!eventsToPropagate?.length

  const modalExited = useCallback(() => {
    setTrapConfig(initialTrapConfig)
    if (modal) {
      bodyScroll.enable(modal)
    }
  }, [modal])

  const [animationDone, setAnimationDone] = useState(false)

  function onAnimationStart() {
    setAnimationDone(false)
  }
  function onAnimationEnd() {
    setAnimationDone(true)
  }
  // This checks the definition (motion variant name) that has completed from the onAnimationEnd callback.
  // We do this to make sure it only invokes the callbacks once even when there are multiple animations.
  function onAnimationCompletions(definition: AnimationDefinition) {
    if (definition === ENTER_SCALE) {
      onShowComplete?.()
    }
    if (definition === EXIT_SCALE) {
      onHideComplete?.()
    }
  }

  useEffect(() => {
    if (modal) {
      const focusSelector = initialFocus || `#${defaultInitialFocusId}`

      if (visible && animationDone) {
        let initialFocusedElement

        if (typeof focusSelector === 'object') {
          if (!focusSelector.current) {
            consoleWarn(
              `The 'initialFocus' prop does not reference a current element - initialFocus.current is undefined.`,
            )
            initialFocusedElement = modal.querySelector<HTMLElement>(
              `#${defaultInitialFocusId}`,
            )
          } else {
            initialFocusedElement = focusSelector.current
          }
        } else {
          initialFocusedElement =
            modal.querySelector<HTMLElement>(focusSelector)
        }

        setTrapConfig({
          enabled: true,
          initialElement: initialFocusedElement!,
        })
        onOpen?.()
      }

      bodyScroll.disable(modal)
    }

    return () => {
      if (modal) {
        modalExited()
      }
    }
  }, [
    animationDone,
    defaultInitialFocusId,
    initialFocus,
    modal,
    modalExited,
    onHideComplete,
    onOpen,
    onShowComplete,
    visible,
  ])

  useEffect(() => {
    mountedRef.current = visible
  }, [visible])

  useEffect(() => {
    return () => {
      if (mountedRef.current) {
        // FRNTEND-4854
        consoleWarn(
          'The Modal component was unmounted while `visible` was `true`. This will prevent exit animation and may have unintended effects for keyboard users.',
        )
      }
    }
  }, [])

  const defaultInitialFocus = (
    <span css={{ outline: 'none' }} id={defaultInitialFocusId} tabIndex={-1} />
  )

  const ModalWithAnimation = (
    <MotionVariant
      initial="initial"
      animate={[ENTER_SCALE, 'enterOpacity']}
      exit={[EXIT_SCALE, 'exitOpacity']}
      onAnimationStart={onAnimationStart}
      onAnimationEnd={(definition: AnimationDefinition) => {
        onAnimationCompletions(definition)
        onAnimationEnd()
      }}
    >
      <NavigationTrap {...trapConfig}>
        <ModalBase
          {...restProps}
          accessibilityTitle={accessibilityTitle}
          forwardedRef={mergeRefs(forwardedRef || undefined, modalCallbackRef)}
          closeButton={closeButton}
          zIndex={0}
          disabled={shrouded}
          height={height}
          width={width}
          role={role}
        >
          {!initialFocus && defaultInitialFocus}
          {children}
        </ModalBase>
      </NavigationTrap>
    </MotionVariant>
  )

  const renderModalBase = () =>
    shouldStopPropagation ? (
      <StopPropagation ignoreEvents={eventsToPropagate}>
        {ModalWithAnimation}
      </StopPropagation>
    ) : (
      ModalWithAnimation
    )

  const ModalWithScrim = (
    <EscapeToClose onClose={onClose} enabled={visible && !shrouded}>
      <Scrim onClick={onClose} visible={visible && !shrouded} zIndex={0} />
      <MotionPresence>{visible && renderModalBase()}</MotionPresence>
    </EscapeToClose>
  )

  return _modalPortalChild ? (
    ModalWithScrim
  ) : (
    <ModalPortal targetId={globalIds.ModalContainer}>
      {ModalWithScrim}
    </ModalPortal>
  )
}

Modal.heights = modalHeights
Modal.widths = modalWidths
Modal.roles = modalRoles

Modal.propTypes = {
  accessibilityTitle: PropTypes.string,
  children: PropTypes.node.isRequired,
  closeButton: PropTypes.node,
  'data-.*': PropTypes.string,
  disableEventPropagation: PropTypes.bool,
  eventsToPropagate: PropTypes.arrayOf(PropTypes.string),
  height: PropTypes.oneOf(Modal.heights),
  initialFocus: PropTypes.oneOfType([
    PropTypes.string,
    CustomPropTypes.ReactRefObject,
  ]),
  onClose: PropTypes.func,
  onHideComplete: PropTypes.func,
  onOpen: PropTypes.func,
  onShowComplete: PropTypes.func,
  role: PropTypes.oneOf(Modal.roles),
  shrouded: PropTypes.bool,
  visible: PropTypes.bool,
  width: PropTypes.oneOf(Modal.widths),
}

Modal.displayName = 'Modal'

Modal.Base = ModalBase
Modal.Close = ModalClose
Modal.Header = ModalHeader
Modal.Body = ModalBody
Modal.Footer = ModalFooter
Modal.Portal = ModalPortal
